
/*********************************************************************
 *
 * WARNING: This shader class is automatically generated, any manual
 * edits will be lost.
 *
 ********************************************************************/
import { ShaderBase, ShaderUniformsContainer, ShaderAttributesContainer } from '../shader';
import { Texture, CubeMapTexture } from '../../texture';
import { Context } from '../../context/Context';
import { mat3, mat4, vec2, vec3, vec4 } from "gl-matrix";
import { ShaderSourceParser } from '../shader-source-parser';

export type GBufferTerrainShaderUniforms = {
	uProjectionMatrix: WebGLUniformLocation,
	uUnjitteredViewProjection: WebGLUniformLocation,
	uPreviousViewProjection: WebGLUniformLocation,
	uViewMatrix: WebGLUniformLocation,
	uModelViewMatrix: WebGLUniformLocation,
	uModelMatrix: WebGLUniformLocation,
	uAlbedoArray: WebGLUniformLocation,
	uNormalArray: WebGLUniformLocation,
	uRoughnessArray: WebGLUniformLocation,
	uSlotColor0: WebGLUniformLocation,
	uSlotColor1: WebGLUniformLocation,
	uSlotColor2: WebGLUniformLocation,
	uSlotColor3: WebGLUniformLocation,
	uSlotTiling0: WebGLUniformLocation,
	uSlotTiling1: WebGLUniformLocation,
	uSlotTiling2: WebGLUniformLocation,
	uSlotTiling3: WebGLUniformLocation,
	uSlotHasAlbedo0: WebGLUniformLocation,
	uSlotHasAlbedo1: WebGLUniformLocation,
	uSlotHasAlbedo2: WebGLUniformLocation,
	uSlotHasAlbedo3: WebGLUniformLocation,
	uSlotRoughnessFallback: WebGLUniformLocation,
	uDitherFade: WebGLUniformLocation
};

export type GBufferTerrainShaderAttributes = {
	aInstanceModelCol0: number,
	aInstanceModelCol1: number,
	aInstanceModelCol2: number,
	aInstanceModelCol3: number,
	aVertexPosition: number,
	aVertexNormal: number,
	aVertexColor: number,
	vWorldPosition: number,
	vWorldNormal: number,
	vSlotWeights: number,
	vClipCurr: number,
	vClipPrev: number
};

export type GBufferTerrainShaderSettings = ('INSTANCED' | 'HAS_NORMAL_ARRAY' | 'HAS_ROUGHNESS_ARRAY' | 'CROSSFADE')[];

export class GBufferTerrainShader extends ShaderBase<GBufferTerrainShaderUniforms, GBufferTerrainShaderAttributes> {

    private static fragmentShaderSource = `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
precision highp sampler2DArray;
#else
precision mediump float;
precision mediump sampler2DArray;
#endif

layout(location = 0) out vec4 gAlbedo;
layout(location = 1) out vec4 gNormal;
layout(location = 2) out vec2 gMotion;

in vec4 vWorldPosition;
in vec3 vWorldNormal;
in vec4 vSlotWeights;
in vec4 vClipCurr;
in vec4 vClipPrev;

uniform sampler2DArray uAlbedoArray;

#ifdef NOODLE_HAS_NORMAL_ARRAY
uniform sampler2DArray uNormalArray;
#endif

#ifdef NOODLE_HAS_ROUGHNESS_ARRAY
uniform sampler2DArray uRoughnessArray;
#endif

uniform vec3 uSlotColor0;
uniform vec3 uSlotColor1;
uniform vec3 uSlotColor2;
uniform vec3 uSlotColor3;

uniform float uSlotTiling0;
uniform float uSlotTiling1;
uniform float uSlotTiling2;
uniform float uSlotTiling3;

uniform int uSlotHasAlbedo0;
uniform int uSlotHasAlbedo1;
uniform int uSlotHasAlbedo2;
uniform int uSlotHasAlbedo3;

uniform float uSlotRoughnessFallback;

#ifdef NOODLE_CROSSFADE
uniform float uDitherFade;
const float bayer4x4[16] = float[](
	 0.0/16.0,  8.0/16.0,  2.0/16.0, 10.0/16.0,
	12.0/16.0,  4.0/16.0, 14.0/16.0,  6.0/16.0,
	 3.0/16.0, 11.0/16.0,  1.0/16.0,  9.0/16.0,
	15.0/16.0,  7.0/16.0, 13.0/16.0,  5.0/16.0
);
#endif

const float TRIPLANAR_POWER = 4.0;

vec3 triplanarWeights(vec3 N) {
	vec3 w = pow(abs(N), vec3(TRIPLANAR_POWER));
	float s = w.x + w.y + w.z;
	return w / max(s, 1e-5);
}

vec3 sampleTriplanarAlbedo(float layer, vec3 worldPos, vec3 triW, float tiling) {
	vec2 uvYZ = worldPos.zy * tiling;
	vec2 uvXZ = worldPos.xz * tiling;
	vec2 uvXY = worldPos.xy * tiling;
	vec3 aYZ = texture(uAlbedoArray, vec3(uvYZ, layer)).rgb;
	vec3 aXZ = texture(uAlbedoArray, vec3(uvXZ, layer)).rgb;
	vec3 aXY = texture(uAlbedoArray, vec3(uvXY, layer)).rgb;
	return aYZ * triW.x + aXZ * triW.y + aXY * triW.z;
}

#ifdef NOODLE_HAS_NORMAL_ARRAY
vec3 sampleTriplanarNormal(float layer, vec3 worldPos, vec3 N, vec3 triW, float tiling) {
	vec2 uvYZ = worldPos.zy * tiling;
	vec2 uvXZ = worldPos.xz * tiling;
	vec2 uvXY = worldPos.xy * tiling;
	vec3 nYZ = texture(uNormalArray, vec3(uvYZ, layer)).rgb * 2.0 - 1.0;
	vec3 nXZ = texture(uNormalArray, vec3(uvXZ, layer)).rgb * 2.0 - 1.0;
	vec3 nXY = texture(uNormalArray, vec3(uvXY, layer)).rgb * 2.0 - 1.0;
	// Whiteout-style triplanar normal blend: inject world normal into projection axis,
	// then sum weighted. This is cheap and close-enough for terrain.
	vec3 wYZ = vec3(nYZ.z * sign(N.x), nYZ.y, nYZ.x);
	vec3 wXZ = vec3(nXZ.x, nXZ.z * sign(N.y), nXZ.y);
	vec3 wXY = vec3(nXY.x, nXY.y, nXY.z * sign(N.z));
	return normalize(wYZ * triW.x + wXZ * triW.y + wXY * triW.z);
}
#endif

#ifdef NOODLE_HAS_ROUGHNESS_ARRAY
float sampleTriplanarRoughness(float layer, vec3 worldPos, vec3 triW, float tiling) {
	vec2 uvYZ = worldPos.zy * tiling;
	vec2 uvXZ = worldPos.xz * tiling;
	vec2 uvXY = worldPos.xy * tiling;
	float rYZ = texture(uRoughnessArray, vec3(uvYZ, layer)).r;
	float rXZ = texture(uRoughnessArray, vec3(uvXZ, layer)).r;
	float rXY = texture(uRoughnessArray, vec3(uvXY, layer)).r;
	return rYZ * triW.x + rXZ * triW.y + rXY * triW.z;
}
#endif

void main(void) {
#ifdef NOODLE_CROSSFADE
	ivec2 pc = ivec2(gl_FragCoord.xy);
	float ditherThreshold = bayer4x4[(pc.x & 3) * 4 + (pc.y & 3)];
	// Positive uDitherFade: fade OUT (discard low bayer values → progressively transparent)
	// Negative uDitherFade: fade IN  (discard high bayer values → complementary pattern)
	if (uDitherFade >= 0.0) {
		if (ditherThreshold < uDitherFade) discard;
	} else {
		if (ditherThreshold >= -uDitherFade) discard;
	}
#endif

	vec3 N = normalize(vWorldNormal);
	vec3 triW = triplanarWeights(N);
	vec3 worldPos = vWorldPosition.xyz;

	float w0 = vSlotWeights.x;
	float w1 = vSlotWeights.y;
	float w2 = vSlotWeights.z;
	float w3 = vSlotWeights.w;

	// Accumulate albedo across slots, skipping near-zero weights.
	vec3 albedo = vec3(0.0);
	if (w0 > 0.001) {
		vec3 a = (uSlotHasAlbedo0 == 1)
			? sampleTriplanarAlbedo(0.0, worldPos, triW, uSlotTiling0)
			: uSlotColor0;
		albedo += a * w0;
	}
	if (w1 > 0.001) {
		vec3 a = (uSlotHasAlbedo1 == 1)
			? sampleTriplanarAlbedo(1.0, worldPos, triW, uSlotTiling1)
			: uSlotColor1;
		albedo += a * w1;
	}
	if (w2 > 0.001) {
		vec3 a = (uSlotHasAlbedo2 == 1)
			? sampleTriplanarAlbedo(2.0, worldPos, triW, uSlotTiling2)
			: uSlotColor2;
		albedo += a * w2;
	}
	if (w3 > 0.001) {
		vec3 a = (uSlotHasAlbedo3 == 1)
			? sampleTriplanarAlbedo(3.0, worldPos, triW, uSlotTiling3)
			: uSlotColor3;
		albedo += a * w3;
	}

	// Metallic packs into albedo.a (terrain is dielectric → metallic = 0).
	gAlbedo = vec4(albedo, 0.0);

	vec3 outN = N;
#ifdef NOODLE_HAS_NORMAL_ARRAY
	vec3 nBlend = vec3(0.0);
	if (w0 > 0.001 && uSlotHasAlbedo0 == 1) nBlend += sampleTriplanarNormal(0.0, worldPos, N, triW, uSlotTiling0) * w0;
	if (w1 > 0.001 && uSlotHasAlbedo1 == 1) nBlend += sampleTriplanarNormal(1.0, worldPos, N, triW, uSlotTiling1) * w1;
	if (w2 > 0.001 && uSlotHasAlbedo2 == 1) nBlend += sampleTriplanarNormal(2.0, worldPos, N, triW, uSlotTiling2) * w2;
	if (w3 > 0.001 && uSlotHasAlbedo3 == 1) nBlend += sampleTriplanarNormal(3.0, worldPos, N, triW, uSlotTiling3) * w3;
	if (length(nBlend) > 1e-3) outN = normalize(nBlend);
#endif

	float roughness = uSlotRoughnessFallback;
#ifdef NOODLE_HAS_ROUGHNESS_ARRAY
	float rBlend = 0.0;
	float rTot = 0.0;
	if (w0 > 0.001) { rBlend += sampleTriplanarRoughness(0.0, worldPos, triW, uSlotTiling0) * w0; rTot += w0; }
	if (w1 > 0.001) { rBlend += sampleTriplanarRoughness(1.0, worldPos, triW, uSlotTiling1) * w1; rTot += w1; }
	if (w2 > 0.001) { rBlend += sampleTriplanarRoughness(2.0, worldPos, triW, uSlotTiling2) * w2; rTot += w2; }
	if (w3 > 0.001) { rBlend += sampleTriplanarRoughness(3.0, worldPos, triW, uSlotTiling3) * w3; rTot += w3; }
	if (rTot > 0.001) roughness = rBlend / rTot;
#endif

	// Negative roughness is the terrain sentinel read by the SSAO pass to skip
	// SSAO on terrain surfaces entirely (avoids face-boundary artifacts on
	// low-tessellation heightmap meshes). The deferred-light pass uses abs().
	gNormal = vec4(outN, -(roughness + 0.001));

	vec2 ndcCurr = vClipCurr.xy / vClipCurr.w;
	vec2 ndcPrev = vClipPrev.xy / vClipPrev.w;
	gMotion = (ndcPrev - ndcCurr) * 0.5;
}
`;

    private static vertexShaderSource = `
uniform mat4 uProjectionMatrix;
uniform mat4 uUnjitteredViewProjection;
uniform mat4 uPreviousViewProjection;

#ifdef NOODLE_INSTANCED
layout(location = 6) in vec4 aInstanceModelCol0;
layout(location = 7) in vec4 aInstanceModelCol1;
layout(location = 8) in vec4 aInstanceModelCol2;
layout(location = 9) in vec4 aInstanceModelCol3;
uniform mat4 uViewMatrix;
#else
uniform mat4 uModelViewMatrix;
uniform mat4 uModelMatrix;
#endif

layout(location = 0) in vec3 aVertexPosition;
layout(location = 1) in vec3 aVertexNormal;
layout(location = 3) in vec4 aVertexColor;

out vec4 vWorldPosition;
out vec3 vWorldNormal;
out vec4 vSlotWeights;
out vec4 vClipCurr;
out vec4 vClipPrev;

void main(void) {
	vec4 pos = vec4(aVertexPosition, 1.0);
	vec4 normal = vec4(aVertexNormal, 0.0);

	vSlotWeights = aVertexColor;

#ifdef NOODLE_INSTANCED
	mat4 instanceModel = mat4(aInstanceModelCol0, aInstanceModelCol1, aInstanceModelCol2, aInstanceModelCol3);
	vWorldPosition = instanceModel * vec4(pos.xyz, 1.0);
	vWorldNormal = normalize(mat3(instanceModel) * normal.xyz);
	gl_Position = uProjectionMatrix * uViewMatrix * vWorldPosition;
#else
	vWorldPosition = uModelMatrix * vec4(pos.xyz, 1.0);
	vWorldNormal = normalize(mat3(uModelMatrix) * normal.xyz);
	vec4 viewPos = uModelViewMatrix * vec4(pos.xyz, 1.0);
	gl_Position = uProjectionMatrix * viewPos;
#endif

	vClipCurr = uUnjitteredViewProjection * vWorldPosition;
	vClipPrev = uPreviousViewProjection * vWorldPosition;
}
`;

    public override get version(): string {
        return '300 es';
    }

    public override get parser(): any {
        return new ShaderSourceParser();
    }

    protected override get warnAttributeMismatch(): boolean {
        return false;
    }

    public uniformsContainer: GBufferTerrainShaderUniformsContainer;
    public attributesContainer: GBufferTerrainShaderShaderAttributesContainer;

    constructor(ctx: Context, settings: GBufferTerrainShaderSettings) {
        super(ctx, GBufferTerrainShader.fragmentShaderSource, GBufferTerrainShader.vertexShaderSource, settings);
        this.uniformsContainer = new GBufferTerrainShaderUniformsContainer(ctx, this);
        this.attributesContainer = new GBufferTerrainShaderShaderAttributesContainer(ctx, this);
    }
}

class GBufferTerrainShaderUniformsContainer extends ShaderUniformsContainer {
    constructor(ctx: Context, private shader: GBufferTerrainShader) {
        super(ctx);
    }

    public setProjectionMatrix(value: mat4 | number[]) : GBufferTerrainShaderUniformsContainer {
        this.setValue('uProjectionMatrix', this.shader.uniforms.uProjectionMatrix, value, 'mat4');
        return this;
    }

    public setUnjitteredViewProjection(value: mat4 | number[]) : GBufferTerrainShaderUniformsContainer {
        this.setValue('uUnjitteredViewProjection', this.shader.uniforms.uUnjitteredViewProjection, value, 'mat4');
        return this;
    }

    public setPreviousViewProjection(value: mat4 | number[]) : GBufferTerrainShaderUniformsContainer {
        this.setValue('uPreviousViewProjection', this.shader.uniforms.uPreviousViewProjection, value, 'mat4');
        return this;
    }

    public setViewMatrix(value: mat4 | number[]) : GBufferTerrainShaderUniformsContainer {
        this.setValue('uViewMatrix', this.shader.uniforms.uViewMatrix, value, 'mat4');
        return this;
    }

    public setModelViewMatrix(value: mat4 | number[]) : GBufferTerrainShaderUniformsContainer {
        this.setValue('uModelViewMatrix', this.shader.uniforms.uModelViewMatrix, value, 'mat4');
        return this;
    }

    public setModelMatrix(value: mat4 | number[]) : GBufferTerrainShaderUniformsContainer {
        this.setValue('uModelMatrix', this.shader.uniforms.uModelMatrix, value, 'mat4');
        return this;
    }

    public setAlbedoArray(texture: Texture) : GBufferTerrainShaderUniformsContainer {
        this.setTextureValue('uAlbedoArray', this.shader.uniforms.uAlbedoArray, texture, 'sampler2DArray');
        return this;
    }

    public setNormalArray(texture: Texture) : GBufferTerrainShaderUniformsContainer {
        this.setTextureValue('uNormalArray', this.shader.uniforms.uNormalArray, texture, 'sampler2DArray');
        return this;
    }

    public setRoughnessArray(texture: Texture) : GBufferTerrainShaderUniformsContainer {
        this.setTextureValue('uRoughnessArray', this.shader.uniforms.uRoughnessArray, texture, 'sampler2DArray');
        return this;
    }

    public setSlotColor0(value: vec3 | number[]) : GBufferTerrainShaderUniformsContainer {
        this.setValue('uSlotColor0', this.shader.uniforms.uSlotColor0, value, 'vec3');
        return this;
    }

    public setSlotColor1(value: vec3 | number[]) : GBufferTerrainShaderUniformsContainer {
        this.setValue('uSlotColor1', this.shader.uniforms.uSlotColor1, value, 'vec3');
        return this;
    }

    public setSlotColor2(value: vec3 | number[]) : GBufferTerrainShaderUniformsContainer {
        this.setValue('uSlotColor2', this.shader.uniforms.uSlotColor2, value, 'vec3');
        return this;
    }

    public setSlotColor3(value: vec3 | number[]) : GBufferTerrainShaderUniformsContainer {
        this.setValue('uSlotColor3', this.shader.uniforms.uSlotColor3, value, 'vec3');
        return this;
    }

    public setSlotTiling0(value: number) : GBufferTerrainShaderUniformsContainer {
        this.setValue('uSlotTiling0', this.shader.uniforms.uSlotTiling0, value, 'float');
        return this;
    }

    public setSlotTiling1(value: number) : GBufferTerrainShaderUniformsContainer {
        this.setValue('uSlotTiling1', this.shader.uniforms.uSlotTiling1, value, 'float');
        return this;
    }

    public setSlotTiling2(value: number) : GBufferTerrainShaderUniformsContainer {
        this.setValue('uSlotTiling2', this.shader.uniforms.uSlotTiling2, value, 'float');
        return this;
    }

    public setSlotTiling3(value: number) : GBufferTerrainShaderUniformsContainer {
        this.setValue('uSlotTiling3', this.shader.uniforms.uSlotTiling3, value, 'float');
        return this;
    }

    public setSlotHasAlbedo0(value: number) : GBufferTerrainShaderUniformsContainer {
        this.setValue('uSlotHasAlbedo0', this.shader.uniforms.uSlotHasAlbedo0, value, 'int');
        return this;
    }

    public setSlotHasAlbedo1(value: number) : GBufferTerrainShaderUniformsContainer {
        this.setValue('uSlotHasAlbedo1', this.shader.uniforms.uSlotHasAlbedo1, value, 'int');
        return this;
    }

    public setSlotHasAlbedo2(value: number) : GBufferTerrainShaderUniformsContainer {
        this.setValue('uSlotHasAlbedo2', this.shader.uniforms.uSlotHasAlbedo2, value, 'int');
        return this;
    }

    public setSlotHasAlbedo3(value: number) : GBufferTerrainShaderUniformsContainer {
        this.setValue('uSlotHasAlbedo3', this.shader.uniforms.uSlotHasAlbedo3, value, 'int');
        return this;
    }

    public setSlotRoughnessFallback(value: number) : GBufferTerrainShaderUniformsContainer {
        this.setValue('uSlotRoughnessFallback', this.shader.uniforms.uSlotRoughnessFallback, value, 'float');
        return this;
    }

    public setDitherFade(value: number) : GBufferTerrainShaderUniformsContainer {
        this.setValue('uDitherFade', this.shader.uniforms.uDitherFade, value, 'float');
        return this;
    }

    protected getTextureUniforms() {
        return [
			{ name: 'uAlbedoArray', uniform: this.shader.uniforms.uAlbedoArray, dataType: 'sampler2DArray' },
			{ name: 'uNormalArray', uniform: this.shader.uniforms.uNormalArray, dataType: 'sampler2DArray' },
			{ name: 'uRoughnessArray', uniform: this.shader.uniforms.uRoughnessArray, dataType: 'sampler2DArray' }
        ];
    }
}

class GBufferTerrainShaderShaderAttributesContainer extends ShaderAttributesContainer {
    constructor(ctx: Context, private shader: GBufferTerrainShader) {
        super(ctx);
    }

    public setInstanceModelCol0(settings: { length: number, offset: number, itemSize: number }) : GBufferTerrainShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aInstanceModelCol0, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setInstanceModelCol1(settings: { length: number, offset: number, itemSize: number }) : GBufferTerrainShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aInstanceModelCol1, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setInstanceModelCol2(settings: { length: number, offset: number, itemSize: number }) : GBufferTerrainShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aInstanceModelCol2, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setInstanceModelCol3(settings: { length: number, offset: number, itemSize: number }) : GBufferTerrainShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aInstanceModelCol3, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setVertexPosition(settings: { length: number, offset: number, itemSize: number }) : GBufferTerrainShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aVertexPosition, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setVertexNormal(settings: { length: number, offset: number, itemSize: number }) : GBufferTerrainShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aVertexNormal, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setVertexColor(settings: { length: number, offset: number, itemSize: number }) : GBufferTerrainShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aVertexColor, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setvWorldPosition(settings: { length: number, offset: number, itemSize: number }) : GBufferTerrainShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.vWorldPosition, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setvWorldNormal(settings: { length: number, offset: number, itemSize: number }) : GBufferTerrainShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.vWorldNormal, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setvSlotWeights(settings: { length: number, offset: number, itemSize: number }) : GBufferTerrainShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.vSlotWeights, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setvClipCurr(settings: { length: number, offset: number, itemSize: number }) : GBufferTerrainShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.vClipCurr, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setvClipPrev(settings: { length: number, offset: number, itemSize: number }) : GBufferTerrainShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.vClipPrev, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }
}